import {
  EditableNavigationItem,
  normalizeNavigationItem,
  serializeNavigationItems,
} from './menu-navigation-item.types';

export const MENU_ITEM_CLIPBOARD_FORMAT = 'phx-menu-navigation-item/v1' as const;

/** localStorage mirror so paste works across same-origin tabs when Clipboard API is restricted. */
export const MENU_ITEM_CLIPBOARD_STORAGE_KEY = 'phx.menuItemClipboard.v1';

/** Source role id + identifier so paste can remap roles across PHX instances. */
export type MenuItemClipboardRoleRef = {
  readonly id: string;
  readonly identifier: string;
};

export type MenuItemClipboardData = {
  readonly items: EditableNavigationItem[];
  readonly roles: MenuItemClipboardRoleRef[];
};

type MenuItemClipboardPayload = {
  readonly format: typeof MENU_ITEM_CLIPBOARD_FORMAT;
  /** One or more root items (whole tree when copying with no selection). */
  readonly items: EditableNavigationItem[];
  /** Role refs used by requiredRoleIds — remapped by identifier on paste. */
  readonly roles?: MenuItemClipboardRoleRef[];
};

function isClipboardPayload(value: unknown): value is MenuItemClipboardPayload {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  const record = value as Record<string, unknown>;
  if (record['format'] !== MENU_ITEM_CLIPBOARD_FORMAT) {
    return false;
  }
  if (Array.isArray(record['items'])) {
    return true;
  }
  // Legacy single-item payloads from earlier builds
  return record['item'] != null;
}

function normalizeRoleRefs(raw: unknown): MenuItemClipboardRoleRef[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  const refs: MenuItemClipboardRoleRef[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
      continue;
    }
    const record = entry as Record<string, unknown>;
    const id = typeof record['id'] === 'string' ? record['id'].trim() : '';
    const identifier =
      typeof record['identifier'] === 'string' ? record['identifier'].trim() : '';
    if (id && identifier) {
      refs.push({ id, identifier });
    }
  }
  return refs;
}

/**
 * Builds a clipboard string for one or more navigation items (including dropdown children).
 */
export function buildMenuItemClipboardText(
  items: EditableNavigationItem[],
  roles: MenuItemClipboardRoleRef[] = [],
): string {
  const sanitized = serializeNavigationItems(items);
  const payload: MenuItemClipboardPayload = {
    format: MENU_ITEM_CLIPBOARD_FORMAT,
    items: sanitized,
    ...(roles.length ? { roles } : {}),
  };
  return JSON.stringify(payload);
}

/**
 * Parses clipboard / storage text into normalized clipboard data, or null if not ours.
 */
export function parseMenuItemClipboardText(text: string): MenuItemClipboardData | null {
  const trimmed = text?.trim();
  if (!trimmed) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (!isClipboardPayload(parsed)) {
      return null;
    }
    const record = parsed as Record<string, unknown>;
    const rawItems = Array.isArray(record['items'])
      ? record['items']
      : record['item'] != null
        ? [record['item']]
        : [];
    if (!rawItems.length) {
      return null;
    }
    return {
      items: rawItems.map(item => normalizeNavigationItem(item)),
      roles: normalizeRoleRefs(record['roles']),
    };
  } catch {
    return null;
  }
}

export function peekMenuItemClipboardFromStorage(): MenuItemClipboardData | null {
  try {
    const stored = localStorage.getItem(MENU_ITEM_CLIPBOARD_STORAGE_KEY);
    return stored ? parseMenuItemClipboardText(stored) : null;
  } catch {
    return null;
  }
}

export async function writeMenuItemClipboard(
  items: EditableNavigationItem[],
  roles: MenuItemClipboardRoleRef[] = [],
): Promise<void> {
  if (!items.length) {
    return;
  }
  const text = buildMenuItemClipboardText(items, roles);
  try {
    localStorage.setItem(MENU_ITEM_CLIPBOARD_STORAGE_KEY, text);
  } catch {
    // Quota / private mode — still try system clipboard
  }
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    // Permission denied — localStorage still enables same-origin paste
  }
}

export async function readMenuItemClipboard(): Promise<MenuItemClipboardData | null> {
  try {
    const text = await navigator.clipboard.readText();
    const fromClipboard = parseMenuItemClipboardText(text);
    if (fromClipboard?.items.length) {
      return fromClipboard;
    }
  } catch {
    // Fall through to storage mirror
  }
  return peekMenuItemClipboardFromStorage();
}

/**
 * Remaps requiredRoleIds using clipboard role refs (source id → identifier → local id).
 * Roles without a matching local identifier are dropped when refs are present.
 */
export function remapRequiredRoleIds(
  items: EditableNavigationItem[],
  sourceIdToLocalId: ReadonlyMap<string, string>,
  hasRoleRefs: boolean,
): EditableNavigationItem[] {
  return items.map(item => remapItemRequiredRoleIds(item, sourceIdToLocalId, hasRoleRefs));
}

function remapItemRequiredRoleIds(
  item: EditableNavigationItem,
  sourceIdToLocalId: ReadonlyMap<string, string>,
  hasRoleRefs: boolean,
): EditableNavigationItem {
  const next: EditableNavigationItem = { ...item };
  if (item.requiredRoleIds?.length) {
    if (hasRoleRefs) {
      next.requiredRoleIds = item.requiredRoleIds
        .map(id => sourceIdToLocalId.get(id.trim()))
        .filter((id): id is string => !!id);
    } else {
      next.requiredRoleIds = [...item.requiredRoleIds];
    }
  }
  if (item.children?.length) {
    next.children = remapRequiredRoleIds(item.children, sourceIdToLocalId, hasRoleRefs);
  }
  return next;
}
